
import React, { createContext, useContext, useState, useEffect } from 'react';
import { Addon, CartItem, MenuItem, StaffPin } from '../types';
import { redis } from '../services/redisService';
import { collection, addDoc, serverTimestamp, onSnapshot, doc, Timestamp } from 'firebase/firestore';
import { signInAnonymously, onAuthStateChanged } from 'firebase/auth';
import { writeWithRateLimit } from '../src/utils/rateLimitedWrite';
import { db, auth } from '../src/firebase';
import { toast } from 'sonner';
import { useLanguage } from './LanguageContext';
import { t } from '../translations';
import { Order, OrderType, PaymentMethod, ServiceType } from '../types';

interface CartContextType {
  cart: CartItem[];
  addToCart: (item: MenuItem, selectedAddons?: Addon[]) => void;
  removeFromCart: (itemId: number, addonKey?: string) => void;
  clearCart: () => void;
  restoreCart: (items: CartItem[]) => void;
  sendOrder: (
    orderType: OrderType,
    paymentMethod: PaymentMethod,
    customerName?: string,
    customerType?: string,
    tableNumber?: string,
    notes?: string,
    staffPin?: string,
    customerPhone?: string,
    pickupTime?: Date | null,
    promoCode?: string
  ) => Promise<string>;
  verifyStaffPin: (pin: string) => Promise<StaffPin | null>;
  sendServiceRequest: (type: ServiceType, tableNumber: string) => Promise<void>;
  currentOrder: Order | null;
  totalItems: number;
  totalPrice: number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { language } = useLanguage();
  const [cart, setCart] = useState<CartItem[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const [currentOrderId, setCurrentOrderId] = useState<string | null>(null);
  const [currentOrder, setCurrentOrder] = useState<Order | null>(null);

  // Initialize Auth
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setUserId(user.uid);
        // Load current order ID for this user
        const savedOrderId = localStorage.getItem(`current_order_id_${user.uid}`);
        if (savedOrderId) {
          setCurrentOrderId(savedOrderId);
        }
      } else {
        signInAnonymously(auth).catch(err => console.error("Anonymous auth error:", err));
      }
    });
    return () => unsubscribe();
  }, []);

  // Load cart from "Redis" on mount
  useEffect(() => {
    const loadCart = async () => {
      if (!userId) return;
      const savedCart = await redis.get<CartItem[]>(`user_cart_${userId}`);
      if (savedCart) {
        setCart(savedCart);
      }
      setIsInitialized(true);
    };
    loadCart();
  }, [userId]);

  // Save cart to "Redis" whenever it changes
  useEffect(() => {
    if (isInitialized && userId) {
      redis.set(`user_cart_${userId}`, cart);
    }
  }, [cart, isInitialized, userId]);

  // Listen to current order
  useEffect(() => {
    if (!currentOrderId || !userId) {
      setCurrentOrder(null);
      return;
    }

    const unsubscribe = onSnapshot(doc(db, 'orders', currentOrderId), (docSnap) => {
      if (docSnap.exists()) {
        setCurrentOrder({ id: docSnap.id, ...docSnap.data() } as Order);
      } else {
        setCurrentOrder(null);
        setCurrentOrderId(null);
        localStorage.removeItem(`current_order_id_${userId}`);
      }
    }, (error) => {
      console.error("Error listening to order:", error);
      if (error.code === 'permission-denied') {
        setCurrentOrder(null);
        setCurrentOrderId(null);
        localStorage.removeItem(`current_order_id_${userId}`);
      }
    });

    return () => unsubscribe();
  }, [currentOrderId, userId]);

  const getAddonKey = (addons?: Addon[]) =>
    addons && addons.length > 0 ? addons.map(a => a.names.en).sort().join(',') : '';

  const addToCart = (item: MenuItem, selectedAddons?: Addon[]) => {
    if (typeof navigator !== 'undefined' && navigator.vibrate) {
        navigator.vibrate(10);
    }

    const newAddonKey = getAddonKey(selectedAddons);
    setCart(prev => {
      const existing = prev.find(i => i.id === item.id && getAddonKey(i.selectedAddons) === newAddonKey);
      if (existing) {
        return prev.map(i =>
          (i.id === item.id && getAddonKey(i.selectedAddons) === newAddonKey)
            ? { ...i, quantity: i.quantity + 1 }
            : i
        );
      }
      return [...prev, { ...item, quantity: 1, selectedAddons: selectedAddons && selectedAddons.length > 0 ? selectedAddons : undefined }];
    });
  };

  const removeFromCart = (itemId: number, addonKey?: string) => {
    if (typeof navigator !== 'undefined' && navigator.vibrate) {
        navigator.vibrate(10);
    }

    setCart(prev => {
      const existing = prev.find(i => i.id === itemId && getAddonKey(i.selectedAddons) === (addonKey ?? ''));
      if (existing && existing.quantity > 1) {
        return prev.map(i =>
          (i.id === itemId && getAddonKey(i.selectedAddons) === (addonKey ?? ''))
            ? { ...i, quantity: i.quantity - 1 }
            : i
        );
      }
      return prev.filter(i => !(i.id === itemId && getAddonKey(i.selectedAddons) === (addonKey ?? '')));
    });
  };

  const clearCart = () => {
    setCart([]);
    if (userId) {
      redis.del(`user_cart_${userId}`);
    }
  };

  const restoreCart = (items: CartItem[]) => {
    setCart(items);
  };

  const totalItems = cart.reduce((acc, item) => acc + item.quantity, 0);
  const totalPrice = cart.reduce((acc, item) => {
    const addonTotal = (item.selectedAddons || []).reduce((sum, a) => sum + a.price, 0);
    return acc + ((item.price + addonTotal) * item.quantity);
  }, 0);

  const verifyStaffPin = async (pin: string): Promise<StaffPin | null> => {
    try {
      const res = await fetch('/api/verify-staff-pin', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin }),
      });
      if (!res.ok) return null;
      const data = await res.json();
      if (!data.valid) return null;
      return {
        pin,
        name: data.name || '',
        discount: typeof data.discount === 'number' ? data.discount : 0,
        active: true,
      };
    } catch (e) {
      console.error('Error verifying staff pin:', e);
      return null;
    }
  };

  const sendOrder = async (
    orderType: OrderType,
    paymentMethod: PaymentMethod,
    customerName?: string,
    customerType?: string,
    tableNumber?: string,
    notes?: string,
    staffPin?: string,
    customerPhone?: string,
    pickupTime?: Date | null,
    promoCode?: string
  ) => {
    if (cart.length === 0) throw new Error('Cart is empty');
    if (!userId) throw new Error('User not authenticated');

    // Double-submit protection: check if order was sent in last 5 seconds
    const lastOrderTime = parseInt(localStorage.getItem('lastOrderTime') || '0');
    if (Date.now() - lastOrderTime < 5000) {
      throw new Error('Заказ уже отправлен. Подождите.');
    }
    localStorage.setItem('lastOrderTime', Date.now().toString());

    // Server-side price validation. Если сервер ответил — он истина в последней
    // инстанции (не даём отправить подделанную цену). Если сервер недоступен —
    // graceful degradation: продолжаем, чтобы заказ не зависал из-за инфраструктуры.
    // PIN передаём на сервер — он сам пересчитает скидку, нельзя доверять клиентскому числу.
    let serverFinalTotal: number | null = null;
    let serverStaffDiscount = 0;
    let serverPromo: { type: 'percent' | 'fixed'; value: number } | null = null;
    try {
      const vRes = await fetch('/api/validate-order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: cart,
          total: totalPrice,
          staffPin: staffPin || undefined,
          promoCode: promoCode || undefined,
        })
      });
      if (vRes.status === 400) {
        const v = await vRes.json().catch(() => ({}));
        // Блюдо закончилось, пока гость выбирал: причина понятная, и говорить
        // о ней надо словами, а не «ошибкой проверки цены».
        if (v.error === 'out_of_stock') {
          const e = new Error(
            t('cart.itemOutOfStock', language).replace('{name}', String(v.itemName || ''))
          );
          (e as any).fromServer = true;
          throw e;
        }
        throw new Error(v.error || 'Price validation failed');
      }
      if (vRes.ok) {
        const v = await vRes.json().catch(() => ({}));
        if (typeof v.serverTotal === 'number') serverFinalTotal = v.serverTotal;
        if (typeof v.staffDiscount === 'number') serverStaffDiscount = v.staffDiscount;
        if (v.promo && (v.promo.type === 'percent' || v.promo.type === 'fixed')) serverPromo = v.promo;
      }
    } catch (err: any) {
      // Отказ сервера пробрасываем гостю, недоступность сервера — нет.
      // Раньше распознавали отказ по английскому тексту, и переведённое
      // сообщение о закончившемся блюде проскочило бы как «сеть недоступна»,
      // а заказ ушёл бы на кухню.
      if (err?.fromServer || (err?.message && /mismatch|not found|No items|Invalid|Too many/i.test(err.message))) {
        throw err;
      }
      console.warn('Validation endpoint unreachable, continuing:', err);
    }

    // Generate a simple 4-digit order number
    const orderNumber = Math.floor(1000 + Math.random() * 9000).toString();

    // Verify staff pin if provided (имя получаем отдельно для отображения в чеке).
    // Скидку и финальную сумму берём с сервера (см. /api/validate-order выше) —
    // клиент не имеет права считать скидку сам, иначе можно подделать.
    let staffName: string | null = null;
    if (staffPin) {
      const staffData = await verifyStaffPin(staffPin);
      if (staffData) {
        staffName = staffData.name;
      }
    }
    const staffDiscount = serverStaffDiscount;
    const originalTotal = totalPrice;
    const finalTotal = serverFinalTotal !== null
      ? serverFinalTotal
      : (staffDiscount > 0 ? Math.round(totalPrice * (1 - staffDiscount / 100)) : totalPrice);

    // Strip undefined fields from cart items — Firestore rejects undefined values
    // Кладём в заказ только то, что нужно кухне, кассе и отчётам. Раньше писали
    // весь MenuItem — описание, картинку, каталог допов: документ раздувался, а
    // каталожное addons путали с выбранными гостем selectedAddons.
    const cleanItems = cart.map(item => {
      const clean: Record<string, any> = {
        id: item.id,
        names: item.names,
        price: item.price,
        quantity: item.quantity,
      };
      if (item.selectedAddons?.length) {
        clean.selectedAddons = item.selectedAddons.map(a => ({ names: a.names, price: a.price }));
      }
      return clean;
    });

    const orderData: Record<string, any> = {
      orderNumber,
      items: cleanItems,
      total: finalTotal,
      status: 'new',
      orderType,
      paymentMethod,
      customerName: customerName || null,
      customerType: customerType || 'regular',
      createdAt: serverTimestamp(),
      tableNumber: tableNumber || null,
      notes: notes || null,
      userId: userId,
      isPaid: false
    };

    // Takeaway-специфичные поля
    if (orderType === 'takeaway') {
      if (customerPhone) orderData.customerPhone = customerPhone;
      if (pickupTime) orderData.pickupTime = Timestamp.fromDate(pickupTime);
    }

    // Add staff discount info if applied
    if (staffDiscount > 0) {
      orderData.originalTotal = originalTotal;
      orderData.staffDiscount = staffDiscount;
      orderData.staffName = staffName;
      orderData.staffPin = staffPin;
    }

    // Промокод мог перестать действовать между «применить» и «оформить»
    // (истёк или выбран лимит) — сервер вернёт promo: null. Заказ уходит по
    // полной цене, и об этом честнее сказать вслух, чем молча списать больше.
    if (promoCode && !serverPromo && serverFinalTotal !== null) {
      toast.warning(t('cart.promoExpired', language));
    }

    // Promo: только если сервер подтвердил применение
    if (serverPromo && promoCode) {
      orderData.promoCode = promoCode.toUpperCase();
      orderData.promoType = serverPromo.type;
      orderData.promoValue = serverPromo.value;
      if (!orderData.originalTotal) orderData.originalTotal = originalTotal;
    }

    try {
      // Заказ пишется вместе со сдвигом анти-спам счётчика — иначе правила
      // его отклонят (см. firestore.rules → Rate Limiting).
      const orderId = await writeWithRateLimit({
        collectionName: 'orders',
        limitCollection: 'order_limits',
        uid: userId,
        data: orderData,
        windowMinutes: 30,
      });
      setCurrentOrderId(orderId);
      localStorage.setItem(`current_order_id_${userId}`, orderId);
      // Промокод считается использованным только после успешной записи заказа.
      // Не ждём ответа и глотаем ошибку: сорванный учёт лимита не повод
      // ломать уже оформленный заказ.
      if (orderData.promoCode) {
        fetch('/api/promo/track', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ orderId }),
        }).catch(() => {});
      }
      clearCart();
      return orderId;
    } catch (error) {
      // permission-denied на этом пути = сработал лимит; выше по стеку
      // UI покажет «слишком часто» вместо общей ошибки.
      console.error("Error sending order:", (error as any)?.code || error);
      throw error;
    }
  };

  const sendServiceRequest = async (type: ServiceType, tableNumber: string) => {
    if (!userId) throw new Error('User not authenticated');
    try {
      await writeWithRateLimit({
        collectionName: 'service_requests',
        limitCollection: 'service_limits',
        uid: userId,
        data: {
          type,
          tableNumber,
          status: 'new',
          createdAt: serverTimestamp(),
          userId: userId,
        },
        windowMinutes: 30,
      });
    } catch (error) {
      console.error("Error sending service request:", (error as any)?.code || error);
      throw error;
    }
  };

  return (
    <CartContext.Provider value={{ cart, addToCart, removeFromCart, clearCart, restoreCart, sendOrder, verifyStaffPin, sendServiceRequest, currentOrder, totalItems, totalPrice }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) throw new Error('useCart must be used within a CartProvider');
  return context;
};
